import React, { useMemo, useState } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Flex,
  VStack,
  Image,
  Button,
  IconButton,
  Center,
  Select,
} from '@chakra-ui/react';
import { localStorageUserAuthInfoKey, useAuthUser, useIsBaseSize } from 'core';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Trans } from '@lingui/macro';
import { ExpertisePointGroupTypeLabel } from 'types';
import { AdminPages } from '../admin.types';

export interface AdminDrawerProps {
  onClose: () => void;
  isOpen: boolean;
  variant?: string;
}
const SELECTED_GROUP_TYPE_KEY = 'selectedExpertisePointGroupType';

export const AdminDrawer: React.FC<AdminDrawerProps> = ({ onClose, isOpen, variant }) => {
  const isBaseSize = useIsBaseSize();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedGroupType, setSelectedGroupType] = useState<number | string>(() => {
    const saved = localStorage.getItem(SELECTED_GROUP_TYPE_KEY);
    return saved ? parseInt(saved, 10) : '';
  });

  const currentPageName = useMemo(() => {
    const parts = location.pathname.split('/');
    if (parts.length < 3) {
      return '';
    }

    return parts[2];
  }, [location, location.pathname]);

  const authUserInfo = useAuthUser();

  const handleGroupTypeChange = (value: string) => {
    if (value === '') {
      setSelectedGroupType('');
      localStorage.removeItem(SELECTED_GROUP_TYPE_KEY);
    } else {
      const numValue = parseInt(value, 10);
      setSelectedGroupType(numValue);
      localStorage.setItem(SELECTED_GROUP_TYPE_KEY, numValue.toString());
    }

    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('expertisePointGroupTypeChanged', {
        detail: { value: value === '' ? null : value },
      }),
    );
  };

  return (
    <Drawer
      closeOnEsc={false}
      variant={variant}
      isOpen={isOpen}
      onClose={onClose}
      placement="left"
      autoFocus={false}
      trapFocus={false}
      blockScrollOnMount={false}
    >
      {isBaseSize && <DrawerOverlay />}
      <DrawerContent>
        {isBaseSize && <DrawerCloseButton />}
        {!isBaseSize && authUserInfo && (authUserInfo.isSuperAdmin || authUserInfo.isAdmin) && (
          <Select
            position="fixed"
            top="10px"
            right="10px"
            width="180px"
            size="sm"
            zIndex={9999}
            value={selectedGroupType}
            onChange={(e) => handleGroupTypeChange(e.target.value)}
          >
            <option value="">
              {' '}
              <Trans> Marka Seçiniz </Trans>
            </option>
            {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </Select>
        )}
        <DrawerHeader borderBottomWidth="1px">
          <Flex align="center" justify="space-between" w="max-content" gap={4}>
            <IconButton onClick={() => navigate(`/`)} icon={<ChevronLeftIcon h={10} w={5} />} ml={4} aria-label="" />
            <Center
              onClick={() => {
                navigate('/');
              }}
              style={{ cursor: 'pointer' }}
            >
              <Image
                src={
                  authUserInfo?.expertisePoint?.groupType === 2
                    ? '/images/auto-kale-s2.png'
                    : '/images/occo-ekspertiz-logo-seffaf.png'
                }
                width={authUserInfo?.expertisePoint?.groupType === 2 ? '167px' : '100px'}
              />
            </Center>
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          <Flex width="100%" height="100%" flexDirection="column">
            <VStack spacing="12px" flex="1 0 0" mb={2} overflowY="auto">
              {authUserInfo && !authUserInfo?.isSuperAdmin && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.PANEL ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.PANEL}`);
                  }}
                >
                  <Trans>Pano</Trans>
                </Button>
              )}

              {authUserInfo && authUserInfo?.isSuperAdmin && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.TRANSACTIONS ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.TRANSACTIONS}`);
                  }}
                >
                  <Trans>Sorgu Geçmişi</Trans>
                </Button>
              )}

              <Button
                width="100%"
                size="sm"
                variant={currentPageName === AdminPages.BALANCE ? 'solid' : 'ghost'}
                onClick={() => {
                  navigate(`/admin/${AdminPages.BALANCE}`);
                }}
              >
                <Trans>Bakiye</Trans>
              </Button>
              <Button
                width="100%"
                size="sm"
                variant={currentPageName === AdminPages.REPORTS ? 'solid' : 'ghost'}
                onClick={() => {
                  navigate(`/admin/${AdminPages.REPORTS}`);
                }}
              >
                <Trans>Raporlar</Trans>
              </Button>

              {authUserInfo && authUserInfo?.isSuperAdmin && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.USERS ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.USERS}`);
                  }}
                >
                  <Trans>Kullanıcılar</Trans>
                </Button>
              )}

              {authUserInfo && authUserInfo?.isSuperAdmin && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.PRICE_LIST ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.PRICE_LIST}`);
                  }}
                >
                  <Trans>Fiyat Listesi</Trans>
                </Button>
              )}
              {authUserInfo && authUserInfo?.isSuperAdmin && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.PRODUCT_PRICE_LIST ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.PRODUCT_PRICE_LIST}`);
                  }}
                >
                  <Trans>Ürün Fiyat Listesi</Trans>
                </Button>
              )}
              {authUserInfo && authUserInfo?.isSuperAdmin && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.PACKAGE_MANAGEMENT ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.PACKAGE_MANAGEMENT}`);
                  }}
                >
                  <Trans>Paket Yönetimi</Trans>
                </Button>
              )}
              {authUserInfo && authUserInfo?.isSuperAdmin && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.EXPERTISE_POINT_MANAGEMENT ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.EXPERTISE_POINT_MANAGEMENT}`);
                  }}
                >
                  <Trans>Bayi Yönetimi</Trans>
                </Button>
              )}
              <Button
                width="100%"
                size="sm"
                variant={currentPageName === AdminPages.ANNOUNCEMENT ? 'solid' : 'ghost'}
                onClick={() => {
                  navigate(`/admin/${AdminPages.ANNOUNCEMENT}`);
                }}
              >
                <Trans>Duyurular</Trans>
              </Button>

              {authUserInfo && (authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
                <>
                  <Button
                    width="100%"
                    size="sm"
                    variant={currentPageName === AdminPages.DICTIONARY ? 'solid' : 'ghost'}
                    onClick={() => {
                      navigate(`/admin/${AdminPages.DICTIONARY}`);
                    }}
                  >
                    <Trans>Not Sözlük Yönetimi</Trans>
                  </Button>
                  <Button
                    width="100%"
                    size="sm"
                    variant={currentPageName === AdminPages.MASTER_NOTE ? 'solid' : 'ghost'}
                    onClick={() => {
                      navigate(`/admin/${AdminPages.MASTER_NOTE}`);
                    }}
                  >
                    <Trans>Ustadan Ustaya Not Yönetimi</Trans>
                  </Button>
                </>
              )}

              <Button
                width="100%"
                size="sm"
                variant={currentPageName === AdminPages.INQUIRIES ? 'solid' : 'ghost'}
                onClick={() => {
                  navigate(`/admin/${AdminPages.INQUIRIES}`);
                }}
              >
                <Trans>Sorgulamalar</Trans>
              </Button>

              {authUserInfo && (authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.FINANCIAL_SUMMARY_REPORT ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.FINANCIAL_SUMMARY_REPORT}`);
                  }}
                >
                  <Trans>Bayi Bakiye Raporu</Trans>
                </Button>
              )}

              {authUserInfo && (authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.INHOUSE_CODE ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.INHOUSE_CODE}`);
                  }}
                >
                  <Trans>Koçan Yönetimi</Trans>
                </Button>
              )}

              {authUserInfo && (authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.CAMPAIGN_MANAGEMENT ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.CAMPAIGN_MANAGEMENT}`);
                  }}
                >
                  <Trans>Kampanya Yönetimi</Trans>
                </Button>
              )}
              {authUserInfo && (authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.CAMPAIGN_MATCHING ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.CAMPAIGN_MATCHING}`);
                  }}
                >
                  <Trans>Kampanya Eşleştirme</Trans>
                </Button>
              )}

              {authUserInfo && (authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && (
                <Button
                  width="100%"
                  size="sm"
                  variant={currentPageName === AdminPages.E_STORE ? 'solid' : 'ghost'}
                  onClick={() => {
                    navigate(`/admin/${AdminPages.E_STORE}`);
                  }}
                >
                  <Trans>E-Mağaza Yönetimi</Trans>
                </Button>
              )}

              {/*   <Box width="100%" mb={4}>
                <LanguageSelector />
              </Box> */}

              <Button
                width="100%"
                size="sm"
                variant="ghost"
                color="red"
                onClick={() => {
                  localStorage.removeItem(localStorageUserAuthInfoKey);
                  window?.location?.reload();
                }}
              >
                <Trans>Çıkış Yap</Trans>
              </Button>
            </VStack>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
